import { Paper, Typography, TextField, Checkbox, Box, Autocomplete } from "@mui/material";
import skillsData from '../data/skills.json'
import { MiscModsEditor, IntegratedWeaponsEditor, OrdnanceField } from './ModEditors'
import { limitsForType } from '../Util/AttachmentLimits'

const appFiles = JSON.parse(localStorage.getItem('appFiles') || '{}');
let skills = skillsData
if (appFiles.skills) { skills = JSON.parse(appFiles.skills) }

const AttachmentEdit = ({ editedItem, index, appFiles, setAppFiles }) => {
    let category = editedItem.Type
    const qualities = JSON.parse(appFiles.qualities)
    const talents = JSON.parse(appFiles.talents)
    const filteredQualities = qualities.filter(q => !q.Type || (q.Type === category))

    const handleEdit = (newItem) => {
        let newFiles = { ...appFiles };
        let newCat = JSON.parse(appFiles['attachments']);
        newCat[index] = newItem;
        newFiles['attachments'] = JSON.stringify(newCat);
        setAppFiles(newFiles);
        localStorage.setItem('appFiles', JSON.stringify(newFiles));
    };

    if (editedItem.BaseMods && !Array.isArray(editedItem.BaseMods)) { editedItem.BaseMods = [editedItem.BaseMods] }
    if (editedItem.AddedMods && !Array.isArray(editedItem.AddedMods)) { editedItem.AddedMods = [editedItem.AddedMods] }

    const setMoralityMods = (patch) => {
        const next = { ...(editedItem.MoralityMods || {}), ...patch }
        Object.keys(next).forEach((k) => { if (next[k] === "" || (Array.isArray(next[k]) && !next[k].length)) delete next[k] })
        handleEdit({ ...editedItem, MoralityMods: Object.keys(next).length ? next : undefined })
    }

    const isCrystal = !!editedItem.IsCrystal
    const hasIntegratedWeapons = !isCrystal && Array.isArray(editedItem.WeaponModifiers) && editedItem.WeaponModifiers.length > 0
    const baseMods = editedItem.BaseMods || []
    const CRYSTAL_STAT_KEYS = ["DAMSET", "CRITSET"]
    const crystalStatMods = baseMods.filter((m) => CRYSTAL_STAT_KEYS.includes(m.Key))
    const otherBaseMods = baseMods.filter((m) => !CRYSTAL_STAT_KEYS.includes(m.Key))
    const getCrystalStat = (key) => baseMods.find((m) => m.Key === key)?.Count ?? ""
    const setCrystalStat = (key, value) => {
        const next = baseMods.filter((m) => m.Key !== key)
        if (value !== "") { next.push({ Key: key, Count: value }) }
        handleEdit({ ...editedItem, BaseMods: next })
    }

    // The attachment's own Type gates which fields show and which items it can
    // install on. Offer the known types plus the current value if it is unusual.
    const attachmentTypeOptions = [...new Set(["Weapon", "Armor", "Gear", "Vehicle", "Mount", ...(category ? [category] : [])])]

    // --- Installation restrictions ---
    const isVehicleAtt = category === "Vehicle" || category === "Mount"

    // The item pool an attachment can be installed on: vehicles for
    // vehicle/mount attachments, otherwise personal weapons/armor/gear. Both the
    // ItemLimit picker and the Category dropdown draw their valid options here.
    const itemLimitOptions = isVehicleAtt
        ? (appFiles.vehicles ? JSON.parse(appFiles.vehicles) : [])
        : [
            ...(appFiles.weapons ? JSON.parse(appFiles.weapons) : []),
            ...(appFiles.armor ? JSON.parse(appFiles.armor) : []),
            ...(appFiles.gear ? JSON.parse(appFiles.gear) : []),
        ]

    const uniqueSorted = (arr) => [...new Set(arr.filter(Boolean))].sort((a, b) => a.localeCompare(b))

    // CategoryLimit: weapon/vehicle categories the attachment may install on.
    // Options are every distinct Categories value across the item pool; current
    // values are folded in so a stored option is never orphaned.
    const catLimit = editedItem.CategoryLimit
    const categoryLimitValues = Array.isArray(catLimit) ? catLimit : (catLimit ? [catLimit] : [])
    const categoryOptions = uniqueSorted([
        ...itemLimitOptions.flatMap((i) => Array.isArray(i.Categories) ? i.Categories : (i.Categories ? [i.Categories] : [])),
        ...categoryLimitValues,
    ])
    const setCategoryLimit = (arr) => {
        const next = { ...editedItem }
        if (arr.length === 0) { delete next.CategoryLimit } else { next.CategoryLimit = arr }
        handleEdit(next)
    }

    // TypeLimit ({ Type: string | string[] }): vehicle Type(s) the attachment may
    // install on. Options are the distinct vehicle Types in the pool.
    const typeLimit = editedItem.TypeLimit?.Type
    const typeLimitValues = Array.isArray(typeLimit) ? typeLimit : (typeLimit ? [typeLimit] : [])
    const typeOptions = uniqueSorted([...itemLimitOptions.map((i) => i.Type), ...typeLimitValues])
    const setTypeLimit = (arr) => {
        const next = { ...editedItem }
        if (arr.length === 0) { delete next.TypeLimit }
        else { next.TypeLimit = { Type: arr.length > 1 ? arr : arr[0] } }
        handleEdit(next)
    }

    const rawItemLimit = editedItem.ItemLimit
    const currentItemLimitKey = rawItemLimit == null
        ? ""
        : (typeof rawItemLimit === "object"
            ? (Array.isArray(rawItemLimit.Key) ? rawItemLimit.Key[0] : rawItemLimit.Key)
            : rawItemLimit)
    const setItemLimit = (key) => {
        const next = { ...editedItem }
        if (!key) { delete next.ItemLimit }
        else { next.ItemLimit = isVehicleAtt ? { Key: key } : key }
        handleEdit(next)
    }

    return (
        <Paper>
            <Autocomplete
                sx={{ marginTop: "10px", width: "50%" }}
                options={attachmentTypeOptions}
                value={editedItem?.Type || null}
                onChange={(event, newValue) => handleEdit({ ...editedItem, Type: newValue })}
                renderInput={(params) => <TextField {...params} label="Type" />}
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Price"
                value={editedItem?.Price || 0}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Price: e.target.value })
                }
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Rarity"
                value={editedItem?.Rarity || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Rarity: e.target.value })
                }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                <span>Restricted:</span>
                <Checkbox
                    checked={!!editedItem?.Restricted}
                    onChange={(e) => handleEdit({ ...editedItem, Restricted: e.target.checked })}
                />
                <span style={{ marginLeft: "20px" }}>Is Crystal:</span>
                <Checkbox
                    checked={!!editedItem?.IsCrystal}
                    onChange={(e) => handleEdit({ ...editedItem, IsCrystal: e.target.checked || undefined })}
                />
                {category === "Vehicle" && (
                    <>
                        <span style={{ marginLeft: "20px" }}>Price × Silhouette:</span>
                        <Checkbox
                            checked={editedItem?.PriceSizeMult === true || editedItem?.PriceSizeMult === "true"}
                            onChange={(e) => handleEdit({ ...editedItem, PriceSizeMult: e.target.checked || undefined })}
                        />
                    </>
                )}
            </Box>
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Hard Points"
                value={editedItem?.HP || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, HP: e.target.value })
                }
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Encumbrance"
                value={editedItem?.Encumbrance || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Encumbrance: e.target.value })
                }
            />
            <Typography sx={{ marginTop: "10px" }}>Installation Restrictions:</Typography>
            {limitsForType(category).map(({ suffix, label }) => (
                <Box key={suffix} sx={{ display: 'flex' }}>
                    {["Min", "Max"].map((bound) => (
                        <TextField
                            key={bound}
                            sx={{ marginTop: "10px", width: "50%" }}
                            label={`${bound} ${label}`}
                            value={editedItem?.[bound + suffix] ?? ""}
                            onChange={(e) => {
                                if (isNaN(e.target.value)) return
                                const next = { ...editedItem, [bound + suffix]: e.target.value }
                                if (e.target.value === "") delete next[bound + suffix]
                                handleEdit(next)
                            }}
                        />
                    ))}
                </Box>
            ))}
            {category !== "Armor" && (
                <Autocomplete
                    multiple
                    fullWidth
                    sx={{ marginTop: "10px" }}
                    options={categoryOptions}
                    value={categoryLimitValues}
                    onChange={(event, newValue) => setCategoryLimit(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Category Limit"
                            helperText="Leave blank to allow any category."
                        />
                    )}
                />
            )}
            {isVehicleAtt && (
                <Autocomplete
                    multiple
                    fullWidth
                    sx={{ marginTop: "10px" }}
                    options={typeOptions}
                    value={typeLimitValues}
                    onChange={(event, newValue) => setTypeLimit(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Vehicle Type Limit"
                            helperText="Leave blank to allow any type."
                        />
                    )}
                />
            )}
            <Autocomplete
                sx={{ marginTop: "10px", width: "70%" }}
                options={[...itemLimitOptions].sort((a, b) => (a.Name || "").localeCompare(b.Name || ""))}
                getOptionLabel={(opt) => (opt?.Name ? `${opt.Name} (${opt.Key})` : "")}
                isOptionEqualToValue={(opt, val) => opt.Key === val.Key}
                value={itemLimitOptions.find((o) => o.Key === currentItemLimitKey) || null}
                onChange={(event, newValue) => setItemLimit(newValue ? newValue.Key : "")}
                renderInput={(params) => (
                    <TextField {...params} label={`Only installable on (specific ${isVehicleAtt ? "vehicle" : "item"})`} />
                )}
            />
            {isVehicleAtt && (
                <OrdnanceField
                    value={editedItem.OrdnanceKeys}
                    onChange={(v) => handleEdit({ ...editedItem, OrdnanceKeys: v })}
                    weapons={appFiles.weapons ? JSON.parse(appFiles.weapons) : []}
                />
            )}

            {isCrystal && (
                <>
                    <Typography sx={{ marginTop: "10px" }}>Crystal Profile:</Typography>
                    <Box sx={{ display: 'flex' }}>
                        <TextField
                            sx={{ marginTop: "10px", width: "50%" }}
                            label="Base Damage"
                            value={getCrystalStat("DAMSET")}
                            onChange={(e) => setCrystalStat("DAMSET", e.target.value)}
                        />
                        <TextField
                            sx={{ marginTop: "10px", width: "50%" }}
                            label="Base Crit"
                            value={getCrystalStat("CRITSET")}
                            onChange={(e) => setCrystalStat("CRITSET", e.target.value)}
                        />
                    </Box>
                </>
            )}

            <Typography>Base Mods:</Typography>
            <MiscModsEditor
                mods={isCrystal ? otherBaseMods : baseMods}
                onChange={(newMods) => handleEdit({ ...editedItem, BaseMods: isCrystal ? [...crystalStatMods, ...newMods] : newMods })}
                integratedTarget={hasIntegratedWeapons}
                skills={skills}
                talents={talents}
                qualities={qualities}
                filteredQualities={filteredQualities}
            />

            <Typography>Added Mods:</Typography>
            <MiscModsEditor
                mods={editedItem.AddedMods || []}
                onChange={(newMods) => handleEdit({ ...editedItem, AddedMods: newMods })}
                integratedTarget={hasIntegratedWeapons}
                skills={skills}
                talents={talents}
                qualities={qualities}
                filteredQualities={filteredQualities}
            />

            <Typography>Morality Mods:</Typography>
            <Box sx={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                <TextField
                    label="Morality At Least"
                    type="number"
                    value={editedItem.MoralityMods?.Min ?? ""}
                    onChange={(e) => setMoralityMods({ Min: e.target.value })}
                    sx={{ flex: 1 }}
                />
                <TextField
                    label="Morality At Most"
                    type="number"
                    value={editedItem.MoralityMods?.Max ?? ""}
                    onChange={(e) => setMoralityMods({ Max: e.target.value })}
                    sx={{ flex: 1 }}
                />
            </Box>
            <MiscModsEditor
                mods={editedItem.MoralityMods?.Mods || []}
                onChange={(newMods) => setMoralityMods({ Mods: newMods })}
                skills={skills}
                talents={talents}
                qualities={qualities}
                filteredQualities={filteredQualities}
            />

            <IntegratedWeaponsEditor
                weaponModifiers={editedItem.WeaponModifiers || []}
                skills={skills}
                qualities={qualities}
                onChange={(newWeapons) => handleEdit({ ...editedItem, WeaponModifiers: newWeapons.length ? newWeapons : undefined })}
            />
        </Paper>
    )
}

export default AttachmentEdit
